/**
 * Remote Config Page (Live Admin Control · B0)
 * Edits the versioned app-config document served to the mobile apps:
 *   GET/PUT /api/v1/admin/app-config  (paths in LIVE_CONTROL_ENDPOINTS)
 * Typed editors for payments / prediction ordering / feature flags, plus a
 * validated raw-JSON editor. Every save publishes a new config version.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService, AdminVoucher } from '../services/apiService';
import {
  AppConfig,
  DEFAULT_APP_CONFIG,
  KNOWN_PAYMENT_METHODS,
  normalizeAppConfig,
  validateAppConfigShape,
} from '../types/appConfig';
import ConfirmModal from '../components/modals/ConfirmModal';
import './RemoteConfigPage.css';

type Tab = 'config' | 'promo' | 'raw';

const prettyJson = (config: AppConfig): string => JSON.stringify(config, null, 2);

/**
 * The CTA-route dropdown. Each option maps a human screen name to the EXACT
 * canonical deeplink from PROMO_V2_CONTRACT.md — the founder never types '//'.
 * '__market__' is the sentinel that reveals the market picker; it is not a route.
 */
const MARKET_ROUTE_SENTINEL = '__market__';
const SCREEN_OPTIONS: Array<{ key: string; label: string; route: string }> = [
  { key: 'none', label: 'None — button just dismisses', route: '' },
  { key: 'home', label: 'Home', route: 'versus://home' },
  { key: 'explore', label: 'Explore / Markets', route: 'versus://explore' },
  { key: 'wallet', label: 'Wallet', route: 'versus://wallet' },
  { key: 'deposit', label: 'Deposit', route: 'versus://deposit' },
  { key: 'profile', label: 'Profile', route: 'versus://profile' },
  { key: 'inbox', label: 'Inbox', route: 'versus://inbox' },
  { key: 'market', label: 'Specific prediction…', route: MARKET_ROUTE_SENTINEL },
];

/** A market as the picker needs it (from getAllMarkets, snake_cased payload). */
interface PickerMarket {
  id: string;
  headline: string;
}

/**
 * Which screen dropdown value matches a stored ctaRoute. A `versus://market/<id>`
 * route resolves to the "Specific prediction…" option; '' → None; anything
 * unrecognised (e.g. a legacy hand-typed route) also parks on "Specific…".
 */
const screenKeyForRoute = (route: string): string => {
  if (!route) return 'none';
  if (route.startsWith('versus://market/')) return 'market';
  const hit = SCREEN_OPTIONS.find((o) => o.route === route && o.route !== MARKET_ROUTE_SENTINEL);
  return hit ? hit.key : 'market';
};

/** The market id embedded in a `versus://market/<id>` route, or '' otherwise. */
const marketIdFromRoute = (route: string): string =>
  route.startsWith('versus://market/') ? route.slice('versus://market/'.length) : '';

/** ISO-8601 (Z) → value for <input type="datetime-local"> in the admin's local tz. */
const isoToLocalInput = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

/** <input type="datetime-local"> value → ISO-8601 UTC string ('' stays ''). */
const localInputToIso = (value: string): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

const RemoteConfigPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('config');

  // Document state
  const [version, setVersion] = useState<number | null>(null);
  const [config, setConfig] = useState<AppConfig>(DEFAULT_APP_CONFIG);
  const [savedJson, setSavedJson] = useState<string>(JSON.stringify(DEFAULT_APP_CONFIG));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<AppConfig | null>(null);

  // Raw JSON editor state
  const [rawText, setRawText] = useState('');
  const [rawDirty, setRawDirty] = useState(false);
  const [rawProblems, setRawProblems] = useState<string[] | null>(null);

  // Typed-editor add rows
  const [newMethod, setNewMethod] = useState('');
  const [newFlagKey, setNewFlagKey] = useState('');

  // Promo composer
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Promo deeplink + voucher pickers (loaded lazily when the Promo tab opens)
  const [markets, setMarkets] = useState<PickerMarket[]>([]);
  const [marketSearch, setMarketSearch] = useState('');
  const [vouchers, setVouchers] = useState<AdminVoucher[]>([]);
  const [pickersLoaded, setPickersLoaded] = useState(false);
  const [pickersErr, setPickersErr] = useState<string | null>(null);

  // 1:1 preview: measured card height so we can replicate iOS's radius clamp
  // (effective radius = min(cornerRadius, cardHeight/2)) — the real bottom shape.
  const promoCardRef = useRef<HTMLDivElement | null>(null);
  const [promoCardHeight, setPromoCardHeight] = useState(0);

  const dirty = useMemo(
    () => rawDirty || JSON.stringify(config) !== savedJson,
    [config, savedJson, rawDirty]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSaveMsg(null);
    try {
      const res = await apiService.getAppConfig();
      setVersion(res.version);
      setConfig(res.config);
      setSavedJson(JSON.stringify(res.config));
      setRawText(prettyJson(res.config));
      setRawDirty(false);
      setRawProblems(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load app config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Lazy-load the market + voucher pickers the first time the Promo tab opens.
  useEffect(() => {
    if (tab !== 'promo' || pickersLoaded) return;
    let cancelled = false;
    (async () => {
      setPickersErr(null);
      try {
        const [mkts, vchs] = await Promise.all([
          apiService.getAllMarkets({ status: 'live' }),
          apiService.getVouchers({ status: 'active' }),
        ]);
        if (cancelled) return;
        const parsed: PickerMarket[] = (mkts.markets ?? [])
          .map((m: Record<string, unknown>) => ({
            id: String(m.id ?? ''),
            headline: String(m.headline ?? m.title ?? m.question ?? m.id ?? ''),
          }))
          .filter((m: PickerMarket) => m.id);
        setMarkets(parsed);
        setVouchers(vchs.vouchers);
        setPickersLoaded(true);
      } catch (err) {
        if (!cancelled) setPickersErr(err instanceof Error ? err.message : 'Failed to load pickers');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, pickersLoaded]);

  // Track the rendered preview-card height so the corner-radius clamp matches
  // iOS. ResizeObserver fires on every content/geometry change (live).
  useEffect(() => {
    const el = promoCardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? el.offsetHeight;
      setPromoCardHeight(h);
    });
    ro.observe(el);
    setPromoCardHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [tab, config.promo.layout]);

  // ── Tabs ──────────────────────────────────────────────────────────────────

  const switchTab = (next: Tab) => {
    if (next === tab) return;
    if (tab === 'raw' && rawDirty) {
      const ok = window.confirm('Discard unapplied raw-JSON edits?');
      if (!ok) return;
      setRawDirty(false);
      setRawProblems(null);
    }
    if (next === 'raw') {
      setRawText(prettyJson(config));
      setRawDirty(false);
      setRawProblems(null);
    }
    setTab(next);
  };

  // ── Raw JSON editor ───────────────────────────────────────────────────────

  /** Parse + structural-validate rawText. Returns the config or null (problems set). */
  const validateRaw = (): AppConfig | null => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rawText);
    } catch (err) {
      setRawProblems([`Not valid JSON: ${err instanceof Error ? err.message : 'parse error'}`]);
      return null;
    }
    const problems = validateAppConfigShape(parsed);
    setRawProblems(problems);
    if (problems.length > 0) return null;
    return normalizeAppConfig(parsed);
  };

  const handleRawFormat = () => {
    try {
      setRawText(JSON.stringify(JSON.parse(rawText), null, 2));
      setRawProblems(null);
    } catch (err) {
      setRawProblems([`Not valid JSON: ${err instanceof Error ? err.message : 'parse error'}`]);
    }
  };

  const handleRawApply = () => {
    const next = validateRaw();
    if (!next) return;
    setConfig(next);
    setRawText(prettyJson(next));
    setRawDirty(false);
  };

  // ── Save (PUT → version++) ────────────────────────────────────────────────

  const requestSave = () => {
    setSaveMsg(null);
    let candidate = config;
    if (tab === 'raw' && rawDirty) {
      const next = validateRaw();
      if (!next) return; // problems rendered in the raw tab
      candidate = next;
    }
    setPendingSave(candidate);
    setConfirmOpen(true);
  };

  const doSave = async () => {
    if (!pendingSave) return;
    setConfirmOpen(false);
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await apiService.saveAppConfig(pendingSave);
      setVersion(res.version);
      setConfig(res.config);
      setSavedJson(JSON.stringify(res.config));
      setRawText(prettyJson(res.config));
      setRawDirty(false);
      setRawProblems(null);
      setLoadError(null);
      setSaveMsg({ kind: 'ok', text: `Published config v${res.version}.` });
    } catch (err) {
      setSaveMsg({ kind: 'err', text: err instanceof Error ? err.message : 'Save failed' });
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };

  // ── Typed editors ─────────────────────────────────────────────────────────

  const setPayments = (patch: Partial<AppConfig['payments']>) =>
    setConfig((c) => ({ ...c, payments: { ...c.payments, ...patch } }));

  const setOrdering = (patch: Partial<AppConfig['predictionOrdering']>) =>
    setConfig((c) => ({ ...c, predictionOrdering: { ...c.predictionOrdering, ...patch } }));

  const moveTopOrder = (index: number, dir: -1 | 1) => {
    const next = [...config.payments.topOrder];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPayments({ topOrder: next });
  };

  const removeTopOrder = (index: number) =>
    setPayments({ topOrder: config.payments.topOrder.filter((_, i) => i !== index) });

  const addTopOrder = () => {
    const method = newMethod.trim();
    if (!method || config.payments.topOrder.includes(method)) return;
    setPayments({ topOrder: [...config.payments.topOrder, method] });
    setNewMethod('');
  };

  const setFlag = (key: string, value: boolean) =>
    setConfig((c) => ({ ...c, featureFlags: { ...c.featureFlags, [key]: value } }));

  const removeFlag = (key: string) =>
    setConfig((c) => {
      const { [key]: _removed, ...rest } = c.featureFlags;
      return { ...c, featureFlags: rest };
    });

  const addFlag = () => {
    const key = newFlagKey.trim();
    if (!key || key in config.featureFlags) return;
    setFlag(key, false);
    setNewFlagKey('');
  };

  const flagKeys = Object.keys(config.featureFlags).sort();

  // ── Promo composer (B3) ───────────────────────────────────────────────────

  const promo = config.promo;

  const setPromo = (patch: Partial<AppConfig['promo']>) =>
    setConfig((c) => ({ ...c, promo: { ...c.promo, ...patch } }));

  const handlePromoImagePick = () => fileInputRef.current?.click();

  const handlePromoImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    setUploadErr(null);
    try {
      const res = await apiService.uploadPromoAsset(file);
      setPromo({ imageUrl: res.url });
    } catch (err) {
      setUploadErr(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // iOS clamp: the whole card is clipped to min(cornerRadius, cardHeight/2), so
  // a short card can never round into a circle. We replicate it against the
  // MEASURED height, so the founder sees the identical bottom shape.
  const effectiveRadius =
    promoCardHeight > 0 ? Math.min(promo.cornerRadius, promoCardHeight / 2) : promo.cornerRadius;
  const radiusClamped = promoCardHeight > 0 && effectiveRadius < promo.cornerRadius;

  // Which screen the current ctaRoute represents (drives the dropdown value).
  const screenKey = screenKeyForRoute(promo.ctaRoute);
  const chosenMarketId = marketIdFromRoute(promo.ctaRoute);
  const chosenMarketHeadline =
    markets.find((m) => m.id === chosenMarketId)?.headline || '';

  // Screen dropdown → write the canonical route. Picking "Specific prediction…"
  // parks ctaRoute on the market prefix until a market is chosen (below).
  const handleScreenChange = (key: string) => {
    const opt = SCREEN_OPTIONS.find((o) => o.key === key);
    if (!opt) return;
    if (opt.route === MARKET_ROUTE_SENTINEL) {
      // keep an existing market id if one is already set; else blank prefix
      setPromo({ ctaRoute: chosenMarketId ? `versus://market/${chosenMarketId}` : 'versus://market/' });
      setMarketSearch(chosenMarketHeadline);
    } else {
      setPromo({ ctaRoute: opt.route });
      setMarketSearch('');
    }
  };

  // Market picker (datalist type-ahead). The <option> value carries the id;
  // its visible label is the headline. Matching either commits the route.
  const handleMarketSearchChange = (value: string) => {
    setMarketSearch(value);
    const hit =
      markets.find((m) => m.id === value) ||
      markets.find((m) => m.headline === value) ||
      markets.find((m) => `${m.headline} · ${m.id}` === value);
    if (hit) setPromo({ ctaRoute: `versus://market/${hit.id}` });
  };

  const voucherWarn =
    promo.voucherCode.trim() &&
    pickersLoaded &&
    vouchers.length > 0 &&
    !vouchers.some((v) => v.code === promo.voucherCode.trim());

  const promoWarnings: string[] = [];
  if (promo.enabled) {
    if (!promo.id.trim()) promoWarnings.push('Promo is enabled but has no id.');
    if (!promo.title.trim()) promoWarnings.push('Promo is enabled but has no title.');
    if (promo.startsAt && promo.endsAt && new Date(promo.endsAt) <= new Date(promo.startsAt)) {
      promoWarnings.push('endsAt is not after startsAt.');
    }
    if (screenKey === 'market' && !chosenMarketId) {
      promoWarnings.push('CTA is set to a specific prediction but none is picked.');
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="remote-config-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Remote Config</h1>
          <p className="page-sub">
            Live app control — payments, prediction ordering, feature flags
            {version !== null && <span className="rc-version-badge">config v{version}</span>}
          </p>
        </div>
        <div className="rc-head-actions">
          {saveMsg && <span className={`rc-save-msg ${saveMsg.kind}`}>{saveMsg.text}</span>}
          <button className="btn btn-secondary" onClick={() => void load()} disabled={loading || saving}>
            ↻ Reload
          </button>
          <button
            className="btn rc-btn-save"
            onClick={requestSave}
            disabled={loading || saving || !dirty}
          >
            {saving ? 'Publishing…' : dirty ? '🚀 Save & Publish' : '✓ Published'}
          </button>
        </div>
      </div>

      {loadError && (
        <div className="rc-banner rc-banner-error">
          Could not load app config: {loadError} — editing defaults; saving will still PUT to the
          backend.
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab === 'config' ? 'active' : ''}`} onClick={() => switchTab('config')}>
          ⚙️ Config
        </button>
        <button className={`tab ${tab === 'promo' ? 'active' : ''}`} onClick={() => switchTab('promo')}>
          📣 Promo {promo.enabled ? '· live' : ''}
        </button>
        <button className={`tab ${tab === 'raw' ? 'active' : ''}`} onClick={() => switchTab('raw')}>
          {'{ } Raw JSON'}
        </button>
      </div>

      {loading ? (
        <div className="rc-loading">Loading app config…</div>
      ) : (
        <>
          {tab === 'config' && (
            <div className="grid-2">
              {/* Payments */}
              <div className="card">
                <div className="card-head">
                  <span className="card-title">💳 Payments</span>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">Top payment methods (app order)</label>
                    {config.payments.topOrder.length === 0 ? (
                      <div className="rc-empty">No methods pinned — the app uses its default order.</div>
                    ) : (
                      <div className="rc-list">
                        {config.payments.topOrder.map((method, i) => (
                          <div key={method} className="rc-list-row">
                            <span className="rc-rank">{i + 1}</span>
                            <code className="rc-code">{method}</code>
                            <div className="rc-row-actions">
                              <button
                                className="rc-icon-btn"
                                title="Move up"
                                disabled={i === 0}
                                onClick={() => moveTopOrder(i, -1)}
                              >
                                ↑
                              </button>
                              <button
                                className="rc-icon-btn"
                                title="Move down"
                                disabled={i === config.payments.topOrder.length - 1}
                                onClick={() => moveTopOrder(i, 1)}
                              >
                                ↓
                              </button>
                              <button
                                className="rc-icon-btn rc-icon-btn-danger"
                                title="Remove"
                                onClick={() => removeTopOrder(i)}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="rc-add-row">
                      <input
                        className="form-input"
                        list="rc-known-methods"
                        placeholder="apple_pay, card, …"
                        value={newMethod}
                        onChange={(e) => setNewMethod(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTopOrder();
                          }
                        }}
                      />
                      <datalist id="rc-known-methods">
                        {KNOWN_PAYMENT_METHODS.filter((m) => !config.payments.topOrder.includes(m)).map(
                          (m) => (
                            <option key={m} value={m} />
                          )
                        )}
                      </datalist>
                      <button className="btn btn-secondary" onClick={addTopOrder} disabled={!newMethod.trim()}>
                        + Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prediction ordering */}
              <div className="card">
                <div className="card-head">
                  <span className="card-title">🎯 Prediction Ordering</span>
                </div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">Mode</label>
                    <select
                      className="form-input rc-select"
                      value={config.predictionOrdering.mode}
                      onChange={(e) =>
                        setOrdering({ mode: e.target.value === 'curated' ? 'curated' : 'backend' })
                      }
                    >
                      <option value="backend">backend — ranking as delivered by the API</option>
                      <option value="curated">curated — admin-managed order (Ordering board)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Seed (integer)</label>
                    <input
                      type="number"
                      step={1}
                      className="form-input"
                      value={String(config.predictionOrdering.seed)}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setOrdering({ seed: Number.isNaN(v) ? 0 : v });
                      }}
                    />
                    <span className="rc-hint">Deterministic shuffle seed for client-side tie-breaking.</span>
                  </div>
                </div>
              </div>

              {/* Feature flags */}
              <div className="card">
                <div className="card-head">
                  <span className="card-title">🚩 Feature Flags</span>
                  <span className="rc-muted">{flagKeys.length} flags</span>
                </div>
                <div className="card-body">
                  {flagKeys.length === 0 ? (
                    <div className="rc-empty">No feature flags defined yet.</div>
                  ) : (
                    <div className="rc-list">
                      {flagKeys.map((key) => (
                        <div key={key} className="rc-list-row">
                          <code className="rc-code rc-grow">{key}</code>
                          <label className="rc-switch">
                            <input
                              type="checkbox"
                              checked={config.featureFlags[key]}
                              onChange={(e) => setFlag(key, e.target.checked)}
                            />
                            <span className="rc-slider" />
                          </label>
                          <button
                            className="rc-icon-btn rc-icon-btn-danger"
                            title="Remove flag"
                            onClick={() => removeFlag(key)}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="rc-add-row">
                    <input
                      className="form-input"
                      placeholder="new_flag_key"
                      value={newFlagKey}
                      onChange={(e) => setNewFlagKey(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addFlag();
                        }
                      }}
                    />
                    <button className="btn btn-secondary" onClick={addFlag} disabled={!newFlagKey.trim()}>
                      + Add flag
                    </button>
                  </div>
                  <span className="rc-hint">New flags start OFF. Removing a flag deletes the key.</span>
                </div>
              </div>
            </div>
          )}

          {tab === 'promo' && (
            <div className="grid-2">
              {/* Composer */}
              <div className="card">
                <div className="card-head">
                  <span className="card-title">📣 Promo Composer</span>
                  <span className={`status ${promo.enabled ? 'green' : 'red'}`}>
                    {promo.enabled ? 'enabled' : 'disabled'}
                  </span>
                </div>
                <div className="card-body">
                  <div className="rc-switch-row" style={{ marginBottom: 22 }}>
                    <div className="rc-switch-label">
                      <strong>Enabled</strong>
                      <span className="rc-hint">Shown in-app within the start/end window below.</span>
                    </div>
                    <label className="rc-switch">
                      <input
                        type="checkbox"
                        checked={promo.enabled}
                        onChange={(e) => setPromo({ enabled: e.target.checked })}
                      />
                      <span className="rc-slider" />
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Promo ID</label>
                    <input
                      className="form-input"
                      placeholder="launch-week"
                      value={promo.id}
                      onChange={(e) => setPromo({ id: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Image</label>
                    <div className="rc-add-row" style={{ marginTop: 0 }}>
                      <input
                        className="form-input"
                        placeholder="https://… (or upload)"
                        value={promo.imageUrl}
                        onChange={(e) => setPromo({ imageUrl: e.target.value })}
                      />
                      <button
                        className="btn btn-secondary"
                        onClick={handlePromoImagePick}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading…' : '⬆ Upload'}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => void handlePromoImageUpload(e)}
                      />
                    </div>
                    {uploadErr && <span className="rc-hint" style={{ color: 'var(--red)' }}>{uploadErr}</span>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input
                      className="form-input"
                      placeholder="Launch Week — double rewards"
                      value={promo.title}
                      onChange={(e) => setPromo({ title: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Body</label>
                    <textarea
                      className="form-input rc-textarea"
                      rows={3}
                      placeholder="Short promo copy shown under the title"
                      value={promo.body}
                      onChange={(e) => setPromo({ body: e.target.value })}
                    />
                  </div>

                  {/* Layout mode */}
                  <div className="form-group">
                    <label className="form-label">Layout</label>
                    <div className="rc-seg">
                      <button
                        type="button"
                        className={`rc-seg-btn ${promo.layout === 'card' ? 'active' : ''}`}
                        onClick={() => setPromo({ layout: 'card' })}
                      >
                        🃏 Card
                      </button>
                      <button
                        type="button"
                        className={`rc-seg-btn ${promo.layout === 'fullscreen' ? 'active' : ''}`}
                        onClick={() => setPromo({ layout: 'fullscreen' })}
                      >
                        🖼 Fullscreen
                      </button>
                    </div>
                    <span className="rc-hint">
                      Card = centered dark-glass card · Fullscreen = edge-to-edge takeover (size/shape ignored).
                    </span>
                  </div>

                  {/* Size / shape — card only */}
                  {promo.layout === 'card' && (
                    <div className="rc-slider-group">
                      <div className="rc-slider-row">
                        <label className="form-label rc-slider-label">
                          Card width <span className="rc-slider-val">{promo.cardWidth}px</span>
                        </label>
                        <input
                          type="range"
                          className="rc-range"
                          min={260}
                          max={430}
                          step={1}
                          value={promo.cardWidth}
                          onChange={(e) => setPromo({ cardWidth: parseInt(e.target.value, 10) })}
                        />
                      </div>
                      <div className="rc-slider-row">
                        <label className="form-label rc-slider-label">
                          Image height <span className="rc-slider-val">{promo.imageHeight}px</span>
                        </label>
                        <input
                          type="range"
                          className="rc-range"
                          min={0}
                          max={420}
                          step={1}
                          value={promo.imageHeight}
                          onChange={(e) => setPromo({ imageHeight: parseInt(e.target.value, 10) })}
                        />
                        <span className="rc-hint">0 = no image area.</span>
                      </div>
                      <div className="rc-slider-row">
                        <label className="form-label rc-slider-label">
                          Corner radius <span className="rc-slider-val">{promo.cornerRadius}px</span>
                        </label>
                        <input
                          type="range"
                          className="rc-range"
                          min={0}
                          max={64}
                          step={1}
                          value={promo.cornerRadius}
                          onChange={(e) => setPromo({ cornerRadius: parseInt(e.target.value, 10) })}
                        />
                        <span className="rc-hint">
                          Fixes the circle bug. The preview clamps to min(radius, cardHeight ÷ 2) exactly like iOS.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="rc-field-pair">
                    <div className="form-group">
                      <label className="form-label">CTA text</label>
                      <input
                        className="form-input"
                        placeholder="Bet now"
                        value={promo.ctaText}
                        onChange={(e) => setPromo({ ctaText: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Max impressions</label>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        className="form-input"
                        value={String(promo.maxImpressions)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setPromo({ maxImpressions: Number.isNaN(v) ? 0 : Math.max(0, v) });
                        }}
                      />
                      <span className="rc-hint">Per user; 0 = unlimited.</span>
                    </div>
                  </div>

                  {/* Where does the CTA go? — screen dropdown builds the canonical route */}
                  <div className="form-group">
                    <label className="form-label">Where does the CTA go?</label>
                    <select
                      className="form-input rc-select"
                      value={screenKey}
                      onChange={(e) => handleScreenChange(e.target.value)}
                    >
                      {SCREEN_OPTIONS.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>

                    {screenKey === 'market' && (
                      <div style={{ marginTop: 12 }}>
                        <input
                          className="form-input"
                          list="rc-promo-markets"
                          placeholder={
                            pickersLoaded ? 'Search a live prediction by headline…' : 'Loading predictions…'
                          }
                          value={marketSearch}
                          onChange={(e) => handleMarketSearchChange(e.target.value)}
                        />
                        <datalist id="rc-promo-markets">
                          {markets.map((m) => (
                            <option key={m.id} value={m.headline}>
                              {m.id}
                            </option>
                          ))}
                        </datalist>
                        {chosenMarketId ? (
                          <span className="rc-hint" style={{ color: 'var(--cyan)' }}>
                            ✓ {chosenMarketHeadline || `market ${chosenMarketId}`}
                          </span>
                        ) : (
                          <span className="rc-hint">Pick a prediction to complete the deeplink.</span>
                        )}
                      </div>
                    )}

                    <div className="rc-route-readout">
                      <span className="rc-muted">resulting ctaRoute:</span>{' '}
                      <code className="rc-code">{promo.ctaRoute || '(none — dismiss only)'}</code>
                    </div>
                    {pickersErr && (
                      <span className="rc-hint" style={{ color: 'var(--red)' }}>
                        Could not load pickers: {pickersErr}
                      </span>
                    )}
                  </div>

                  {/* Voucher — real voucher picker + free-text (David: "Beides") */}
                  <div className="form-group">
                    <label className="form-label">Voucher (optional)</label>
                    <div className="rc-add-row" style={{ marginTop: 0 }}>
                      <input
                        className="form-input"
                        placeholder="Free-text code, e.g. LAUNCH50"
                        value={promo.voucherCode}
                        maxLength={64}
                        onChange={(e) => setPromo({ voucherCode: e.target.value })}
                      />
                      <select
                        className="form-input rc-select rc-preset-select"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) setPromo({ voucherCode: e.target.value });
                        }}
                      >
                        <option value="">
                          {vouchers.length ? 'Pick real voucher…' : pickersLoaded ? 'No active vouchers' : 'Loading…'}
                        </option>
                        {vouchers.map((v) => (
                          <option key={v.id} value={v.code}>
                            {v.code}
                            {v.amount != null ? ` · ${v.amount} ${v.currency}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span className="rc-hint">
                      Shown in-app as a copyable code row. Clear the box for no voucher.
                      {voucherWarn && (
                        <>
                          {' '}
                          <span style={{ color: 'var(--red)' }}>
                            ⚠ not an active voucher — will still ship as a literal code.
                          </span>
                        </>
                      )}
                    </span>
                  </div>

                  <div className="rc-field-pair">
                    <div className="form-group">
                      <label className="form-label">Starts at</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={isoToLocalInput(promo.startsAt)}
                        onChange={(e) => setPromo({ startsAt: localInputToIso(e.target.value) })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ends at</label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={isoToLocalInput(promo.endsAt)}
                        onChange={(e) => setPromo({ endsAt: localInputToIso(e.target.value) })}
                      />
                    </div>
                  </div>
                  <span className="rc-hint">
                    Stored as UTC (ISO-8601). {promo.startsAt && `starts ${promo.startsAt}`}
                    {promo.endsAt && ` · ends ${promo.endsAt}`}
                  </span>

                  {promoWarnings.length > 0 && (
                    <ul className="rc-problems" style={{ marginTop: 18 }}>
                      {promoWarnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* 1:1 preview — phone-framed, driven by the SAME config values +
                  iOS constants the app uses, so it is genuinely pixel-faithful. */}
              <div className="card">
                <div className="card-head">
                  <span className="card-title">📱 Live 1:1 preview</span>
                  <span className="rc-muted">SF Pro · exact iOS PromoOverlayView constants</span>
                </div>
                <div className="card-body">
                  <div className={`rc-phone ${promo.enabled ? '' : 'rc-phone-off'}`}>
                    <div className="rc-phone-notch" />
                    <div className="rc-phone-screen">
                      <div className="rc-statusbar">
                        <span>9:41</span>
                        <span className="rc-statusbar-right">􀫗 􀖀 􀛨</span>
                      </div>

                      {/* Backdrop: black 0.55 full screen */}
                      <div className="rc-overlay-backdrop">
                        {promo.layout === 'card' ? (
                          /* ── CARD ── centered, 24px side padding from screen edge ── */
                          <div className="rc-card-wrap">
                            <div
                              ref={promoCardRef}
                              className="rc-glass-card"
                              style={{
                                width: promo.cardWidth,
                                maxWidth: '100%',
                                borderRadius: effectiveRadius,
                              }}
                            >
                              {/* Close button — top-trailing, 32x32, 10px inset */}
                              <button className="rc-overlay-close" type="button" aria-label="Close">
                                ✕
                              </button>

                              {promo.imageHeight > 0 &&
                                (promo.imageUrl ? (
                                  <img
                                    className="rc-card-image"
                                    src={promo.imageUrl}
                                    alt=""
                                    style={{ height: promo.imageHeight }}
                                  />
                                ) : (
                                  <div
                                    className="rc-card-image rc-card-image-empty"
                                    style={{ height: promo.imageHeight }}
                                  >
                                    no image
                                  </div>
                                ))}

                              <div className="rc-card-text">
                                <div className="rc-card-title">{promo.title || 'Promo title'}</div>
                                <div className="rc-card-body">
                                  {promo.body || 'Promo body copy shown under the title.'}
                                </div>

                                {promo.voucherCode.trim() && (
                                  <div className="rc-voucher-pill">
                                    <span className="rc-voucher-code">{promo.voucherCode.trim()}</span>
                                    <span className="rc-voucher-copy" aria-hidden>
                                      ⧉
                                    </span>
                                  </div>
                                )}

                                <button className="rc-glass-cta" type="button">
                                  {promo.ctaText || 'Continue'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* ── FULLSCREEN ── image cover fills frame, bottom scrim ── */
                          <div className="rc-fs">
                            {promo.imageUrl ? (
                              <img className="rc-fs-image" src={promo.imageUrl} alt="" />
                            ) : (
                              <div className="rc-fs-image rc-fs-image-empty">no image</div>
                            )}
                            <button className="rc-overlay-close rc-fs-close" type="button" aria-label="Close">
                              ✕
                            </button>
                            <div className="rc-fs-scrim">
                              <div className="rc-card-title">{promo.title || 'Promo title'}</div>
                              <div className="rc-card-body">
                                {promo.body || 'Promo body copy shown over the bottom scrim.'}
                              </div>
                              {promo.voucherCode.trim() && (
                                <div className="rc-voucher-pill">
                                  <span className="rc-voucher-code">{promo.voucherCode.trim()}</span>
                                  <span className="rc-voucher-copy" aria-hidden>
                                    ⧉
                                  </span>
                                </div>
                              )}
                              <button className="rc-glass-cta" type="button">
                                {promo.ctaText || 'Continue'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rc-preview-facts">
                    {!promo.enabled && <span className="rc-fact rc-fact-off">disabled — not shown in-app</span>}
                    <span className="rc-muted">
                      id: {promo.id || '—'} · max {promo.maxImpressions || '∞'} impressions ·{' '}
                      {promo.layout}
                    </span>
                    {promo.layout === 'card' && (
                      <span className="rc-muted">
                        radius {promo.cornerRadius}px
                        {radiusClamped ? (
                          <> → clamped to {Math.round(effectiveRadius)}px (½ card height)</>
                        ) : (
                          <> (fits)</>
                        )}{' '}
                        · card {promo.cardWidth}×{Math.round(promoCardHeight)}px
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'raw' && (
            <div className="card">
              <div className="card-head">
                <span className="card-title">{'{ }'} Raw JSON</span>
                <span className="rc-muted">
                  Unknown keys are preserved — safe for fields newer than this panel.
                </span>
              </div>
              <div className="card-body">
                <textarea
                  className="rc-json-editor"
                  spellCheck={false}
                  rows={22}
                  value={rawText}
                  onChange={(e) => {
                    setRawText(e.target.value);
                    setRawDirty(true);
                    setRawProblems(null);
                  }}
                />
                <div className="rc-raw-actions">
                  <button className="btn btn-secondary" onClick={handleRawFormat}>
                    Format
                  </button>
                  <button className="btn btn-secondary" onClick={() => void validateRaw()}>
                    Validate
                  </button>
                  <button className="btn btn-secondary" onClick={handleRawApply} disabled={!rawDirty}>
                    Apply to editors
                  </button>
                  {rawProblems !== null && rawProblems.length === 0 && (
                    <span className="rc-valid">✓ Valid config document</span>
                  )}
                </div>
                {rawProblems !== null && rawProblems.length > 0 && (
                  <ul className="rc-problems">
                    {rawProblems.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={confirmOpen}
        title="Publish app config?"
        message={
          version !== null
            ? `This publishes config v${version} → v${version + 1} and goes live to all app clients immediately.`
            : 'This publishes the config document and goes live to all app clients immediately.'
        }
        confirmText="Publish"
        cancelText="Cancel"
        confirmButtonClass="primary"
        onConfirm={() => void doSave()}
        onCancel={() => {
          setConfirmOpen(false);
          setPendingSave(null);
        }}
      />
    </div>
  );
};

export default RemoteConfigPage;
